// Go to Definition for SQL models that no SqlMonaco owns.
//
// Every mounted SqlMonaco registers a DefinitionProvider for its language id,
// and Monaco runs every provider for every model of that language. Each one
// therefore answers only for its OWN editor's model (`isOwnModel`), or one F12
// would navigate once per mounted tab. That leaves the SQL models no SqlMonaco
// owns without an answer, so this module is their single responder: one
// provider per language id, registered once per renderer, which declines every
// model a SqlMonaco has claimed (`registerDefinitionModelOwner`). Exactly one
// provider answers for any model.
//
//  - A Peek References preview (`jamsql-ref:`, a referencing object's stored
//    body) resolves in full against the object's own connection, database and
//    engine, taken from its cache entry.
//  - A plain Monaco SQL editor whose host attached its connection context
//    (`definitionModelContext.ts` — job step / job script / import script
//    viewers, the PL/SQL editor and debugger, schema- and data-compare script
//    previews) resolves in full against that context.
//  - Any other unowned model (schema-compare / blueprint diffs and viewers,
//    which show two sides or repository files, not one live database) gets
//    the buffer-local kinds only: alias and CTE jumps. The dialect then comes
//    from the language id (`plsql` → Oracle, otherwise the scanners' own
//    T-SQL default).
//
// Before this module every unowned model was answered by EVERY mounted query
// tab against that tab's own connection — one navigation per tab.

import type * as monacoNs from 'monaco-editor';

import { DATABASE_ENGINE_IDS } from '@/core/engines';
import type { DatabaseEngineId } from '@/core/engines';

import { getConnectionById } from '../../connections/utils/connectionLookup';
import { lookupPeekReferenceModel } from '../find-references/peekReferenceModels';
import { isPeekReferenceScheme } from '../find-references/peekReferenceUri';
import { lookupDefinitionModelContext } from './definitionModelContext';
import type { DefinitionNavMonacoApi } from './definitionNavRegistry';
import { createDefinitionProvider } from './definitionProvider';

/** Structural subset of the Monaco namespace the install needs. */
export type UnownedModelDefinitionMonacoApi = DefinitionNavMonacoApi & {
  readonly languages: {
    registerDefinitionProvider: (
      selector: monacoNs.languages.LanguageSelector,
      provider: monacoNs.languages.DefinitionProvider,
    ) => monacoNs.IDisposable;
  };
};

type ModelGetter = () => monacoNs.editor.ITextModel | null;

const owners = new Set<ModelGetter>();
const installedByLanguage = new Map<string, monacoNs.IDisposable>();

/** A SqlMonaco claims whatever model its editor currently shows (read live, so
 *  model swaps need no bookkeeping). Returns the release for unmount. */
export function registerDefinitionModelOwner(getModel: ModelGetter): () => void {
  owners.add(getModel);
  return () => {
    owners.delete(getModel);
  };
}

export function isModelOwnedByEditor(model: monacoNs.editor.ITextModel): boolean {
  for (const getModel of owners) {
    if (getModel() === model) return true;
  }
  return false;
}

/** Idempotent per language id; lives as long as the renderer. */
export function installUnownedModelDefinitionProvider(
  monaco: UnownedModelDefinitionMonacoApi,
  languageId: string,
): void {
  if (installedByLanguage.has(languageId)) return;
  const disposable = monaco.languages.registerDefinitionProvider(languageId, {
    provideDefinition: (model, position, token) =>
      provideUnownedModelDefinition(monaco, languageId, model, position, token),
  });
  installedByLanguage.set(languageId, disposable);
}

/** Test-only — dispose every registration and forget owners and latches. */
export function __resetUnownedModelDefinitionProviderForTest(): void {
  for (const disposable of installedByLanguage.values()) disposable.dispose();
  installedByLanguage.clear();
  owners.clear();
}

export function provideUnownedModelDefinition(
  monaco: DefinitionNavMonacoApi,
  languageId: string,
  model: monacoNs.editor.ITextModel,
  position: monacoNs.Position,
  token: monacoNs.CancellationToken,
): monacoNs.languages.ProviderResult<monacoNs.languages.Definition | monacoNs.languages.LocationLink[]> {
  if (isModelOwnedByEditor(model)) return null;

  if (isPeekReferenceScheme(model.uri.scheme)) {
    const entry = lookupPeekReferenceModel(model.uri.toString());
    if (!entry) return null;
    return createDefinitionProvider({
      engineId: entry.engineId,
      getDocumentId: () => entry.uri,
      getConnectionId: () => entry.connectionId,
      getDatabase: () => entry.database || null,
      monaco,
    }).provideDefinition(model, position, token);
  }

  // With a host context: full resolution. Without one, no connection →
  // `handleProvideDefinition` stops after the alias / CTE dispatch.
  const context = lookupDefinitionModelContext(model);
  return createDefinitionProvider({
    engineId: context?.engineId ?? engineOfConnection(context?.connectionId) ?? dialectForLanguage(languageId),
    getDocumentId: () => model.uri.toString(),
    getConnectionId: () => context?.connectionId ?? null,
    getDatabase: () => context?.database ?? null,
    monaco,
  }).provideDefinition(model, position, token);
}

function engineOfConnection(connectionId: string | undefined): DatabaseEngineId | null {
  if (!connectionId) return null;
  const { connection, engine } = getConnectionById(connectionId);
  if (!connection) return null;
  return DATABASE_ENGINE_IDS.find((id) => id === engine) ?? null;
}

function dialectForLanguage(languageId: string): DatabaseEngineId {
  return languageId === 'plsql' ? 'oracle' : 'mssql';
}
